import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BookOpen, ArrowLeft, Search, Sparkles, Clock, X, Bookmark, Layers, GraduationCap, BookMarked, LayoutGrid, ChevronRight, Video, FileText, Presentation, Star, SlidersHorizontal } from 'lucide-react';
import { db } from '../../firebase';
import { collection, getDocs, query, where, orderBy, updateDoc, doc } from 'firebase/firestore';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import ContentViewer from '../shared/ContentViewer';
import { SkeletonFeaturedCard } from '../shared/Skeleton';
import { isViewableContent } from '../../utils/educationContent';
import { useBackHandler } from '../../hooks/NavigationContext';
import { useEducationLibrary, shelfItems } from '../../utils/educationLibrary';
import { ContentRail, ContentRows } from '../shared/ContentCard';
import CollectionCard, { CollectionRail } from '../shared/CollectionCard';
import { useCollections, COLLECTION_KINDS } from '../../utils/educationCollections';
import { TOPICS, topicByLabel, topicNav } from '../../data/educationTaxonomy';
import TopicIcon from '../shared/icons/TopicIcon';
import { EMPTY_FACETS, facetsActive, facetCount, searchItems, searchCollections, suggestTopics, levelsIn, groupByKind, toMillis } from '../../utils/educationSearch';
import { trackEducationView, trackVideoPlay, trackPDFView, trackPresentationView } from '../../utils/analyticsUtils';
import { useSearchLog } from '../../utils/telemetry';

/*
 * Education Center home. Four zones, in order: Resume (Saved & Recent), Discover (Featured,
 * Curricula, Series, Reading Lists, New This Month), Browse (one card to the topic grid) and
 * Find (the search field with facets). Typing or choosing a facet turns the page into results.
 */

const RAIL_CAP = 8;
const FORMATS = [
  { id: 'video', label: 'Videos', icon: Video },
  { id: 'presentation', label: 'Slides', icon: Presentation },
  { id: 'document', label: 'PDFs', icon: FileText },
];
const KIND_RAILS = [
  { kind: 'curriculum', icon: GraduationCap },
  { kind: 'series', icon: Layers },
  { kind: 'reading-list', icon: BookMarked },
];

const Chip = ({ on, onClick, icon: Icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={on}
    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium border transition-colors active:scale-95 ${
      on
        ? 'bg-emerald-700 border-emerald-700 text-white shadow-sm'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-emerald-400'
    }`}
  >
    {Icon && <Icon size={13} className={on ? 'text-white' : 'text-gray-500 dark:text-gray-400'} />}
    {children}
  </button>
);

const Eyebrow = ({ icon: Icon, tone = 'text-emerald-600 dark:text-emerald-400', children, onViewAll }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon size={18} className={tone} />
    <h2 className="eyebrow flex-1">{children}</h2>
    {onViewAll && (
      <button onClick={onViewAll} className="text-[13px] font-semibold text-emerald-700 dark:text-emerald-400 hover:underline">View all</button>
    )}
  </div>
);

const EducationCenterScreen = ({ onBack, onTopicClick, onCollectionClick, onBrowseClick }) => {
  const [searchQuery, setSearchQuery] = useLocalStorage('education-center-search', '');
  const [facets, setFacets] = useLocalStorage('education-center-facets', EMPTY_FACETS);
  // Open while the field or the facet bar has focus. Chips swallow pointerdown so a tap on
  // them never blurs the field; the topic picker takes focus itself and counts as inside.
  const [facetsOpen, setFacetsOpen] = useState(false);
  const facetsRef = useRef(null);
  const leaveSearch = (e) => { if (!(facetsRef.current && facetsRef.current.contains(e.relatedTarget))) setFacetsOpen(false); };
  const [featuredContent, setFeaturedContent] = useState([]);
  const [allContent, setAllContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState(null);
  const { saved, recent } = useEducationLibrary();
  const { collections } = useCollections();
  const closeViewer = useCallback(() => setSelectedContent(null), []);
  useBackHandler(Boolean(selectedContent && selectedContent.fileType !== 'document'), closeViewer);

  // Older builds stored facets without every key
  const f = useMemo(() => ({ ...EMPTY_FACETS, ...(facets || {}) }), [facets]);
  const setF = (patch) => setFacets({ ...f, ...patch });
  const clearFind = useCallback(() => { setSearchQuery(''); setFacets(EMPTY_FACETS); }, [setSearchQuery, setFacets]);
  const finding = searchQuery.trim().length > 0 || facetsActive(f);
  // Results are a layer over the home: Back closes them before leaving the Education Center
  useBackHandler(finding && !selectedContent, clearFind);
  const showFacets = finding || facetsOpen;

  // Lock body scroll while the viewer is open
  useEffect(() => {
    if (selectedContent && selectedContent.fileType !== 'document') {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
    return undefined;
  }, [selectedContent]);

  // Analytics for opened content
  useEffect(() => {
    if (!selectedContent) return;
    const contentType = selectedContent.fileType || 'unknown';
    const contentTitle = selectedContent.title || 'Untitled';
    const contentUrl = selectedContent.muxPlaybackId ? `mux:${selectedContent.muxPlaybackId}` : (selectedContent.downloadURL || '');
    trackEducationView(contentType, contentTitle, selectedContent.id);
    if (contentType === 'video') trackVideoPlay(contentTitle, contentUrl);
    else if (contentType === 'document') trackPDFView(contentTitle, contentUrl);
    else if (contentType === 'presentation') trackPresentationView(contentTitle, contentUrl);
  }, [selectedContent]);

  useEffect(() => {
    loadFeaturedContent();
    loadCatalogue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The whole catalogue, once per visit: rails, search and facets all read from it
  const loadCatalogue = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'education_content')));
      const list = [];
      snap.forEach((d) => {
        const data = { id: d.id, ...d.data() };
        if (isViewableContent(data)) list.push(data);
      });
      setAllContent(list);
    } catch (error) {
      console.error('Error loading catalogue:', error);
    }
  };

  const loadFeaturedContent = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'education_content'), where('featured', '==', true), orderBy('uploadDate', 'desc'));
      const snap = await getDocs(q);
      const allFeatured = [];
      snap.forEach((d) => allFeatured.push({ id: d.id, ...d.data() }));
      setFeaturedContent(allFeatured.filter(isViewableContent).slice(0, RAIL_CAP));
      // Keep the featured set to the newest RAIL_CAP; older ones quietly stop being featured
      if (allFeatured.length > RAIL_CAP) {
        for (const item of allFeatured.slice(RAIL_CAP)) {
          // eslint-disable-next-line no-await-in-loop
          await updateDoc(doc(db, 'education_content', item.id), { featured: false });
        }
      }
    } catch (error) {
      console.error('Error loading featured content:', error);
    } finally {
      setLoading(false);
    }
  };

  const openItem = (item) => { if (searchQuery.trim()) markPicked(item.id); setSelectedContent(item); };
  const openCollection = (c) => { if (searchQuery.trim()) markPicked(c.id); onCollectionClick(c); };
  const getCategoryColor = (category) => (topicByLabel(category) || {}).color || 'from-gray-500 to-gray-600';

  // Home-view data
  const shelf = shelfItems(saved, recent, allContent);
  const shelfAll = useMemo(() => shelfItems(saved, recent, allContent, Infinity), [saved, recent, allContent]);
  const shelfOrder = useMemo(() => shelfAll.map((i) => i.id), [shelfAll]);
  const monthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const newAll = useMemo(() => [...allContent]
    .filter((item) => toMillis(item.uploadDate) > monthAgo && !item.featured)
    .sort((a, b) => toMillis(b.uploadDate) - toMillis(a.uploadDate)), [allContent, monthAgo]);
  const byKind = (kind) => collections.filter((c) => c.kind === kind);

  // Results data
  const savedIds = useMemo(() => new Set(saved.map((s) => s.id)), [saved]);
  const results = useMemo(() => (finding ? searchItems(allContent, searchQuery, f, savedIds, shelfOrder) : []), [finding, allContent, searchQuery, f, savedIds, shelfOrder]);
  const resultCollections = useMemo(() => (finding ? searchCollections(collections, searchQuery, f) : []), [finding, collections, searchQuery, f]);
  const grouped = groupByKind(results);
  const levels = levelsIn(allContent);
  const suggestions = finding && results.length === 0 && resultCollections.length === 0 ? suggestTopics(searchQuery) : [];
  const { markPicked } = useSearchLog('education', searchQuery, allContent.length ? results.length + resultCollections.length : null);
  const topicOptions = TOPICS;

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 dark:from-emerald-900 dark:to-black text-white p-6 pb-6">
        <button onClick={() => { onBack(); }} className="flex items-center gap-2 text-white mb-4 hover:opacity-80">
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-amber-400/20 rounded-xl">
            <BookOpen size={32} className="text-amber-300" />
          </div>
          <div>
            <h1 className="font-merriweather text-3xl font-bold">Education Center</h1>
            <p className="text-emerald-100 text-sm mt-1">Evidence-based learning resources</p>
          </div>
        </div>

        <div className="bg-emerald-900/40 dark:bg-gray-800/60 rounded-xl border-2 border-emerald-700/50 dark:border-gray-700 focus-within:border-amber-400/50 dark:focus-within:border-blue-500 transition-colors">
          <div className="relative">
            <Search className="absolute left-4 top-3.5 text-emerald-300 dark:text-gray-400" size={20} />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setFacetsOpen(true)}
              onBlur={leaveSearch}
              placeholder="Search lectures, documents, series"
              enterKeyHint="search"
              className="w-full pl-12 pr-11 py-3.5 rounded-xl focus:outline-none text-white placeholder-emerald-300 dark:placeholder-gray-500 bg-transparent [&::-webkit-search-cancel-button]:hidden"
            />
            {finding && (
              <button onClick={clearFind} aria-label="Clear search and filters" className="absolute right-2 top-2 p-1.5 rounded-full text-emerald-200 hover:bg-white/10">
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Facets: appear when the field is active or anything is applied */}
      {showFacets && (
        <div
          ref={facetsRef}
          className="px-6 pt-4 space-y-2"
          onPointerDown={(e) => { if (e.target.closest('select') == null) e.preventDefault(); }}
          onBlurCapture={leaveSearch}
        >
          <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-6 px-6 pb-1">
            {FORMATS.map((fm) => (
              <Chip key={fm.id} icon={fm.icon} on={f.types.includes(fm.id)} onClick={() => setF({ types: f.types.includes(fm.id) ? f.types.filter((t) => t !== fm.id) : [...f.types, fm.id] })}>
                {fm.label}
              </Chip>
            ))}
            <Chip icon={Star} on={f.featured} onClick={() => setF({ featured: !f.featured })}>Featured</Chip>
            <Chip icon={Clock} on={f.recent} onClick={() => setF({ recent: !f.recent })}>New this month</Chip>
            <Chip icon={Bookmark} on={f.shelf} onClick={() => setF({ shelf: !f.shelf, saved: false })}>Saved & Recent</Chip>
            {KIND_RAILS.map(({ kind, icon: Icon }) => (
              <Chip key={kind} icon={Icon} on={f.kind === kind} onClick={() => setF({ kind: f.kind === kind ? null : kind })}>{COLLECTION_KINDS[kind].plural}</Chip>
            ))}
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-6 px-6 pb-1 items-center">
            <div className="relative flex-shrink-0">
              <select
                value={f.topic || ''}
                onChange={(e) => setF({ topic: e.target.value || null })}
                aria-label="Topic"
                className={`appearance-none pl-3 pr-8 py-1.5 rounded-full text-[13px] font-medium border focus:outline-none ${
                  f.topic ? 'bg-emerald-700 border-emerald-700 text-white' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                <option value="">Any topic</option>
                {topicOptions.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <ChevronRight size={14} className={`absolute right-2.5 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none ${f.topic ? 'text-white' : 'text-gray-500'}`} />
            </div>
            {levels.map((lv) => (
              <Chip key={lv} on={f.levels.includes(lv)} onClick={() => setF({ levels: f.levels.includes(lv) ? f.levels.filter((x) => x !== lv) : [...f.levels, lv] })}>
                {lv}
              </Chip>
            ))}
            {facetsActive(f) && (
              <button onClick={() => setFacets(EMPTY_FACETS)} className="flex-shrink-0 inline-flex items-center gap-1 text-[13px] font-semibold text-gray-500 dark:text-gray-400 px-2">
                <SlidersHorizontal size={13} /> Clear {facetCount(f)}
              </button>
            )}
          </div>
        </div>
      )}

      {finding ? (
        /* ---------------- Results ---------------- */
        <div className="px-6 pb-8 pt-4 space-y-6">
          {resultCollections.length > 0 && (
            <div>
              <h2 className="eyebrow mb-3">{f.kind ? COLLECTION_KINDS[f.kind].plural : 'Series & Curricula'} ({resultCollections.length})</h2>
              <div className="space-y-3">
                {resultCollections.map((c) => <CollectionCard key={c.id} collection={c} variant="row" onOpen={openCollection} />)}
              </div>
            </div>
          )}
          {f.shelf && !searchQuery.trim() && results.length > 0 && (
            <div>
              <h2 className="eyebrow mb-3">Saved & Recent ({results.length})</h2>
              <ContentRows items={results} onOpen={openItem} shelfOf={(item) => (shelfAll.find((x) => x.id === item.id) || {}).shelf} />
            </div>
          )}
          {!(f.shelf && !searchQuery.trim()) && FORMATS.map((fm) => {
            const list = grouped[fm.id];
            if (!list.length) return null;
            return (
              <div key={fm.id}>
                <h2 className="eyebrow mb-3">{fm.label} ({list.length})</h2>
                <ContentRows items={list} onOpen={openItem} />
              </div>
            );
          })}
          {results.length === 0 && resultCollections.length === 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 text-center">
              <Search size={36} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-gray-700 dark:text-gray-200 font-medium">
                {searchQuery.trim() ? `Nothing matches "${searchQuery.trim()}"${facetsActive(f) ? ' with these filters' : ''}` : 'Nothing matches these filters'}
              </p>
              {suggestions.length > 0 && (
                <div className="mt-4 text-left">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Related topics</p>
                  <div className="grid grid-cols-2 gap-2">
                    {suggestions.map((t) => (
                      <button key={t.id} onClick={() => onTopicClick(topicNav(t))} className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-emerald-500 text-left">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${t.color} text-white flex items-center justify-center flex-shrink-0`}><TopicIcon type={t.icon} className="w-4 h-4" /></div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white leading-tight">{t.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={onBrowseClick} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                <LayoutGrid size={15} /> Browse all content
              </button>
            </div>
          )}
        </div>
      ) : (
        /* ---------------- Home ---------------- */
        <>
          <div className="px-6 pt-6">
            <button
              onClick={() => { onBrowseClick(); }}
              className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 shadow-card hover:border-emerald-500 dark:hover:border-emerald-400 hover:shadow-card-hover transition-all flex items-center gap-4 text-left active:scale-[0.99]"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white flex items-center justify-center flex-shrink-0 shadow-md">
                <LayoutGrid size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 dark:text-white">Browse All Content</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{TOPICS.length} topics, by specialty</div>
              </div>
              <ChevronRight size={20} className="text-gray-400 dark:text-gray-500" />
            </button>
          </div>

          {shelf.length > 0 && (
            <div className="px-6 pt-6">
              <Eyebrow icon={Bookmark} onViewAll={() => setF({ shelf: true })}>Saved & Recent</Eyebrow>
              <ContentRail items={shelf} onOpen={openItem} shelfOf={(item) => item.shelf} width="w-52" />
            </div>
          )}

          {featuredContent.length > 0 && (
            <div className="px-6 pt-6">
              <Eyebrow icon={Sparkles} tone="text-amber-500" onViewAll={() => setF({ featured: true })}>Featured</Eyebrow>
              <ContentRail items={featuredContent} onOpen={openItem} width="w-52" hideFeatured />
            </div>
          )}

          {loading && featuredContent.length === 0 && (
            <div className="px-6 pt-6">
              <Eyebrow icon={Sparkles} tone="text-amber-500">Featured</Eyebrow>
              <div className="flex gap-3 overflow-hidden"><SkeletonFeaturedCard /><SkeletonFeaturedCard /></div>
            </div>
          )}

          {KIND_RAILS.map(({ kind, icon: Icon }) => {
            const list = byKind(kind);
            if (!list.length) return null;
            return (
              <div key={kind} className="px-6 pt-6">
                <Eyebrow icon={Icon} onViewAll={() => setF({ kind })}>{COLLECTION_KINDS[kind].plural}</Eyebrow>
                <CollectionRail collections={list} onOpen={onCollectionClick} />
              </div>
            );
          })}

          {newAll.length > 0 && (
            <div className="px-6 pt-6 pb-8">
              <Eyebrow icon={Clock} onViewAll={() => setF({ recent: true })}>New This Month</Eyebrow>
              <ContentRail items={newAll.slice(0, RAIL_CAP)} onOpen={openItem} width="w-52" />
            </div>
          )}
          {newAll.length === 0 && <div className="pb-8" />}
        </>
      )}

      {selectedContent && selectedContent.fileType !== 'document' && (
        <ContentViewer content={selectedContent} accent={getCategoryColor(selectedContent.category)} onClose={closeViewer} />
      )}

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default EducationCenterScreen;
